import { isSessionEndedError } from '@papan/api-client'
import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Clock, RotateCcw, X, Loader2, FileText, AlertCircle } from 'lucide-react'
import { useTranslation } from '@papan/i18n'
import { useConfirm, useToast, useMyCapabilities, useRetentionPurgeConfirm, RetentionPurgeReceipt, type RetentionPurgeOutcomeView } from '@papan/core-ui/ui'
import { sessionSignal } from '@papan/core-ui/ui/sessionEnded'
import { useRealtime } from '@papan/realtime/client'
import { filesApi, type FileVersion } from '@papan/files/client'
import { restoreErrorText } from './restoreErrorText'

type RetentionSource = 'file' | 'ext' | 'kind' | 'tenant' | 'platform' | 'shipped'

interface VersionRetentionState {
  override: number | null
  resolved: number
  source: RetentionSource
  /** The AUTHORITATIVE kind the resolver used — `'pdapp'` for a file nested
   *  in a `.pdapp` folder, never derivable from the filename alone (a pdapp
   *  file is atomic: its own extension is ignored). Always present. */
  kind: string
  /** The extension the resolver used, or `null` (no extension / a pdapp
   *  file, whose extension layer is skipped entirely). */
  ext: string | null
  /** #1493: `false` for a file reached through a route into ANOTHER org — its retention is the owning org's setting
   *  (owner 2026-10-30): shown, never editable here, whatever this org lets the viewer manage. Absent ⇒ editable. */
  editable?: boolean
}

/** Blank → inherit (not a set), else a whole number in [1, 500]. */
function parseRetentionInput(text: string): number | null {
  const trimmed = text.trim()
  if (trimmed === '') return null
  const n = Number(trimmed)
  if (!Number.isInteger(n) || n < 1 || n > 500) return null
  return n
}

interface VersionPanelProps {
  fileId: string
  fileName: string
  /** The tenant this file lives in — gates the "Keep versions" section on
   *  `manage_org:write` (#590). Required so a new host cannot forget to wire
   *  it and silently show retention controls to every member. */
  tenantId: string
  onClose: () => void
  onPreview?: (versionId: string, isLatest: boolean) => void
  /** Controlled selected version — kept in sync with preview overlay */
  selectedVersion?: string | null
  /** Whether the current file has unsaved changes */
  isDirty?: boolean
  /** Callback to discard unsaved changes (used when latest version is selected) */
  onDiscard?: () => void
  /** Navigate to Settings → Version retention, scrolled to this file's
   *  per-file override row (#590 follow-up). Absent = hidden, never a dead
   *  control — the "Manage" button only renders when a caller wires it. */
  onManageRetention?: () => void
}

function formatRelativeTime(dateStr: string): string {
  const now = Date.now()
  const then = new Date(dateStr).getTime()
  const diffMs = now - then
  const diffSec = Math.floor(diffMs / 1000)

  if (diffSec < 60) return 'just now'
  const diffMin = Math.floor(diffSec / 60)
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDay = Math.floor(diffHr / 24)
  if (diffDay < 30) return `${diffDay}d ago`
  return new Date(dateStr).toLocaleDateString()
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function VersionPanel({ fileId, fileName, tenantId, onClose, onPreview, selectedVersion, isDirty, onDiscard, onManageRetention }: VersionPanelProps) {
  const [versions, setVersions] = useState<FileVersion[]>([])
  const [resolvedFileId, setResolvedFileId] = useState(fileId)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Use controlled selectedVersion prop if provided, otherwise internal state
  const [internalSelectedId, setInternalSelectedId] = useState<string | null>(null)
  const selectedVersionId = selectedVersion !== undefined ? selectedVersion : internalSelectedId
  const setSelectedVersionId = selectedVersion !== undefined
    ? (_v: string | null) => {} // no-op: parent controls via onPreview
    : setInternalSelectedId
  const [restoring, setRestoring] = useState(false)

  const confirm = useConfirm()
  const toast = useToast()
  const { t } = useTranslation('files')

  // ── Per-file "Keep versions" control (#590 Task 10) ──────────────────────
  // Admin-only (#590 gating pass): changing another member's retention window
  // is `manage_org:write`, not a fact every viewer of the file history should
  // see. `isPersonal` short-circuits `can()` to true (implicit owner); an org
  // member without the capability gets neither the section nor the fetch.
  const isPersonal = tenantId.startsWith('p_')
  const { can } = useMyCapabilities(tenantId, isPersonal, undefined)
  const canManageRetention = can('manage_org', 'write')

  const [retention, setRetention] = useState<VersionRetentionState | null>(null)
  const [retentionLoading, setRetentionLoading] = useState(true)
  const [retentionError, setRetentionError] = useState<string | null>(null)
  const [retentionInput, setRetentionInput] = useState('')
  const [retentionSaving, setRetentionSaving] = useState(false)
  const [retentionActionError, setRetentionActionError] = useState<string | null>(null)
  // #1443 — a lowered limit is previewed and confirmed before it deletes anything
  const purgeConfirm = useRetentionPurgeConfirm({ testId: 'file-retention-purge' })
  const [purgeOutcome, setPurgeOutcome] = useState<RetentionPurgeOutcomeView | null>(null)
  /** `fetchVersions` is declared below the retention handlers; they refresh the list through this. */
  const fetchVersionsRef = useRef<(() => Promise<void>) | null>(null)

  const fetchRetention = useCallback(async () => {
    setRetentionLoading(true)
    setRetentionError(null)
    let reasked = false
    try {
      const result = await filesApi.getVersionRetention(resolvedFileId) as VersionRetentionState
      setRetention(result)
      setRetentionInput(result.override != null ? String(result.override) : '')
    } catch (err) {
      if (isSessionEndedError(err)) { reasked = true; void fetchRetention(); return }   // #1316: asked again in the new session
      setRetentionError(t('versionPanel.retention.loadFailed'))
      console.error('[VersionPanel] Failed to load version retention:', err)
    } finally {
      if (!reasked) setRetentionLoading(false)
    }
    // `resolvedFileId`, NOT `fileId` — a `.pmeta` sidecar shares its PARENT's
    // version history (the parent is what `cleanupOldVersions` prunes and what
    // `restoreVersion` targets, see `fetchVersions` below). Keying retention on
    // the raw `fileId` would store/read the override against the sidecar while
    // enforcement runs on the parent — a shown-vs-enforced split. `resolvedFileId`
    // starts as `fileId` (so a normal file fetches immediately and correctly)
    // and only diverges once `fetchVersions` resolves a sidecar to its parent.
    // Deliberately not `t`: keying a fetch off the translator reference would
    // refetch on every render under an unmemoized `t` (e.g. a test's i18n mock).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedFileId])

  // Fetch for EVERYONE who can open the history (the GET needs only file
  // READ): a non-admin still SEES the resolved "keeping N versions, from X"
  // line — only the editable field + Set/Clear/Manage buttons below are
  // admin-gated (#590 gating pass, refined).
  useEffect(() => {
    fetchRetention()
  }, [fetchRetention])

  const parsedRetentionInput = parseRetentionInput(retentionInput)

  /** Human-readable "from …" phrase for the resolved layer. Uses the
   *  SERVER's `kind`/`ext` (Task 8 GET response), never a client-side
   *  re-derivation from `fileName` — a file nested in a `.pdapp` folder
   *  resolves under the atomic `'pdapp'` kind regardless of its own
   *  extension, which `fileKind(fileName)` has no way to know. */
  const retentionSourceLabel = useMemo(() => {
    if (!retention) return ''
    switch (retention.source) {
      case 'file': return t('versionPanel.retention.source.file')
      case 'tenant': return t('versionPanel.retention.source.tenant')
      case 'platform': return t('versionPanel.retention.source.platform')
      case 'shipped': return t('versionPanel.retention.source.shipped')
      case 'ext': {
        return retention.ext
          ? t('versionPanel.retention.source.ext', { ext: retention.ext })
          : t('versionPanel.retention.source.extUnknown')
      }
      case 'kind': {
        const group = t(`versionPanel.retention.kindLabel.${retention.kind}`)
        return t('versionPanel.retention.source.kind', { group })
      }
      default: return ''
    }
  }, [retention, t])

  const handleSetRetention = useCallback(async () => {
    if (parsedRetentionInput == null) return
    setRetentionSaving(true)
    setRetentionActionError(null)
    try {
      // #1443 — previewed (the purge's own plan for this file), confirmed, then saved with `purge: true`
      const res = await purgeConfirm.run(
        () => filesApi.versionRetentionPurgePreview(resolvedFileId, parsedRetentionInput),
        (purge) => filesApi.setVersionRetention(resolvedFileId, parsedRetentionInput, { purge }),
      )
      if (res === null) return
      setPurgeOutcome(res.purged || res.queued ? res : null)
      await fetchRetention()
      if (res.purged?.versions) await fetchVersionsRef.current?.()
    } catch (err) {
      setRetentionActionError(t('versionPanel.retention.setFailed'))
      console.error('[VersionPanel] Failed to set version retention:', err)
    } finally {
      setRetentionSaving(false)
    }
  }, [resolvedFileId, parsedRetentionInput, fetchRetention, t, purgeConfirm.run])

  const handleClearRetention = useCallback(async () => {
    setRetentionSaving(true)
    setRetentionActionError(null)
    try {
      // removing the override can LOWER the limit (an override above the extension rule) — the same confirmed purge
      const res = await purgeConfirm.run(
        () => filesApi.versionRetentionPurgePreview(resolvedFileId, null),
        (purge) => filesApi.clearVersionRetention(resolvedFileId, { purge }),
      )
      if (res === null) return
      setPurgeOutcome(res.purged || res.queued ? res : null)
      await fetchRetention()
      if (res.purged?.versions) await fetchVersionsRef.current?.()
    } catch (err) {
      setRetentionActionError(t('versionPanel.retention.clearFailed'))
      console.error('[VersionPanel] Failed to clear version retention:', err)
    } finally {
      setRetentionSaving(false)
    }
  }, [resolvedFileId, fetchRetention, t, purgeConfirm.run])

  const fetchVersions = useCallback(async () => {
    setLoading(true)
    setError(null)
    let reasked = false
    try {
      const result = await filesApi.getVersions(fileId)
      setVersions(result.versions)
      if (result.resolvedFileId) setResolvedFileId(result.resolvedFileId)
    } catch (err) {
      if (isSessionEndedError(err)) { reasked = true; void fetchVersions(); return }   // #1316: asked again in the new session
      setError('Failed to load versions')
      console.error('[VersionPanel] Failed to load versions:', err)
    } finally {
      if (!reasked) setLoading(false)
    }
  }, [fileId])
  fetchVersionsRef.current = fetchVersions
  // a dialog opened for another file never confirms against this one
  const resetPurge = purgeConfirm.reset
  useEffect(() => { resetPurge(); setPurgeOutcome(null) }, [fileId, resetPurge])

  useEffect(() => {
    fetchVersions()
    setSelectedVersionId(null)
  }, [fetchVersions])

  // Auto-refresh when file changes (agent writes, edits, restores, etc.)
  const { onFileEvent } = useRealtime()
  useEffect(() => {
    return onFileEvent((event) => {
      if (event.fileId === fileId && (event.type === 'file:updated' || event.type === 'file:restored')) {
        fetchVersions()
      }
    })
  }, [fileId, onFileEvent, fetchVersions])

  const handleSelectVersion = useCallback((versionId: string, isLatest: boolean) => {
    // Clicking the already-selected version deselects it
    if (selectedVersionId === versionId) {
      setSelectedVersionId(null)
      onPreview?.('', false)
      return
    }
    setSelectedVersionId(versionId)
    onPreview?.(versionId, isLatest)
  }, [selectedVersionId, onPreview])

  const handleRestore = useCallback(async () => {
    if (!selectedVersionId) return

    const confirmed = await confirm({
      title: 'Restore Version',
      message: 'This will replace the current content with the selected version. Any unsaved changes will be lost.',
      type: 'warning',
      confirmText: 'Restore',
      cancelText: 'Cancel',
    })

    if (!confirmed) return

    // E7 PR 0b (D134): the restore runs to completion; only what it SHOWS is dropped once the session ended
    // (no toast, no state change, no refetch: this panel may be kept alive for the old account)
    const signal = sessionSignal()
    setRestoring(true)
    try {
      const restored = await filesApi.restoreVersion(resolvedFileId, selectedVersionId)
      if (signal.aborted) return
      // #1090 — a large app database is restored in the background; its outcome arrives as a notice.
      toast.success(restored?.queued ? 'Restoring the database in the background — you will be notified when it is done' : 'Version restored successfully')
      setSelectedVersionId(null)
      onPreview?.('', false)  // Close the preview overlay
      await fetchVersions()
    } catch (err) {
      if (signal.aborted) return
      // #1090 / #1134 — the db is being replaced (or a save is still landing); #1397 — a damaged stored copy: the
      // server's own words say which
      toast.error(restoreErrorText(err, 'Failed to restore version'))
      console.error('[VersionPanel] Restore failed:', err)
    } finally {
      if (!signal.aborted) setRestoring(false)
    }
  }, [fileId, selectedVersionId, confirm, toast, fetchVersions])

  return (
    <div className="h-full flex flex-col themed-bg-secondary">
      {/* Header */}
      <div className="h-10 border-b themed-border flex items-center justify-between px-3 shrink-0">
        <div className="flex items-center gap-1.5">
          <Clock size={14} className="themed-text-muted" />
          <span className="text-xs themed-text-muted uppercase tracking-wide">History</span>
        </div>
        <button
          onClick={onClose}
          className="p-0.5 rounded hover:themed-bg-tertiary themed-text-muted hover:themed-text transition-colors"
          title="Close version history"
          aria-label="Close version history"
        >
          <X size={12} />
        </button>
      </div>

      {/* File name */}
      <div className="px-3 py-2 border-b themed-border shrink-0">
        <div className="flex items-center gap-1.5">
          <FileText size={12} className="themed-text-muted shrink-0" />
          <span className="text-xs themed-text truncate">{fileName}</span>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 size={20} className="animate-spin themed-text-muted" />
        </div>
      ) : error ? (
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center">
            <AlertCircle size={24} className="mx-auto mb-2 text-red-400 opacity-60" />
            <p className="text-xs text-red-400">{error}</p>
            <button
              onClick={fetchVersions}
              className="mt-2 text-xs themed-text-muted hover:themed-text underline"
            >
              Retry
            </button>
          </div>
        </div>
      ) : versions.length === 0 ? (
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center themed-text-muted">
            <Clock size={24} className="mx-auto mb-2 opacity-30" />
            <p className="text-xs">No versions yet</p>
            <p className="text-[10px] mt-1 opacity-60">Versions are created each time you save</p>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex flex-col overflow-hidden min-h-0">
          {/* Version list */}
          <div className="overflow-y-auto flex-1">
            {versions.map((version, index) => {
              const isLatest = index === 0
              return (
              <button
                key={version.id}
                onClick={() => handleSelectVersion(version.id, isLatest)}
                className={`w-full text-left px-3 py-2 border-b themed-border transition-colors ${
                  selectedVersionId === version.id
                    ? 'themed-bg-tertiary'
                    : 'hover:themed-bg-tertiary/50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs themed-text font-medium">
                    v{version.versionNumber ?? versions.length - index}
                    {isLatest && <span className="ml-1 text-[10px] themed-text-muted">(current)</span>}
                  </span>
                  <span className="text-[10px] themed-text-muted">
                    {formatRelativeTime(version.createdAt)}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-0.5">
                  <span className="text-[10px] themed-text-muted truncate mr-2">
                    {version.description || version.source || 'Auto-save'}
                  </span>
                  <span className="text-[10px] themed-text-muted shrink-0">
                    {formatBytes(version.size)}
                  </span>
                </div>
                {version.createdBy && (
                  <div className="text-[10px] themed-text-muted mt-0.5 truncate opacity-60">
                    {version.createdBy.email || version.createdBy.name}
                  </div>
                )}
              </button>
              )
            })}
          </div>

          {/* Restore / Discard button */}
          {selectedVersionId && (() => {
            const isLatestSelected = versions.length > 0 && selectedVersionId === versions[0].id
            if (isLatestSelected) {
              return (
                <div className="px-3 py-2 border-t themed-border shrink-0">
                  <button
                    onClick={() => { onDiscard?.(); setSelectedVersionId(null); onPreview?.('', false) }}
                    disabled={!isDirty}
                    className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs rounded
                      bg-amber-600 hover:bg-amber-500 text-white transition-colors
                      disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <RotateCcw size={12} />
                    <span>Discard Changes</span>
                  </button>
                </div>
              )
            }
            return (
              <div className="px-3 py-2 border-t themed-border shrink-0">
                <button
                  onClick={handleRestore}
                  disabled={restoring}
                  className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs rounded
                    bg-amber-600 hover:bg-amber-500 text-white transition-colors
                    disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {restoring ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <RotateCcw size={12} />
                  )}
                  <span>{restoring ? 'Restoring...' : 'Restore this version'}</span>
                </button>
              </div>
            )
          })()}
        </div>
      )}

      {/* Keep versions — per-file retention (#590 Task 10). The SECTION and its
          resolved "keeping N versions, from X" line are shown to everyone who
          can open the history (read-only info); only the editable field +
          Set/Clear/Manage buttons are admin-gated on `manage_org:write`
          (#590 gating pass, refined so a member still SEES the value they
          cannot change rather than the section vanishing). */}
      <div className="px-3 py-2 border-t themed-border shrink-0">
        <span className="text-[10px] themed-text-muted uppercase tracking-wide">
          {t('versionPanel.retention.title')}
        </span>
        {retentionLoading ? (
          <div className="mt-1.5 flex items-center">
            <Loader2 size={12} className="animate-spin themed-text-muted" />
          </div>
        ) : retentionError ? (
          <p className="mt-1 text-[10px] text-red-400">{retentionError}</p>
        ) : retention && (
          <>
            <p className="mt-1 text-[10px] themed-text-muted leading-relaxed">
              {t('versionPanel.retention.resolved', { count: retention.resolved, source: retentionSourceLabel })}
            </p>
            {canManageRetention && retention.editable !== false && (
              <>
                <div className="mt-1.5 flex items-center gap-1.5">
                  <input
                    type="number"
                    min={1}
                    max={500}
                    value={retentionInput}
                    onChange={(e) => setRetentionInput(e.target.value)}
                    placeholder={t('versionPanel.retention.inputPlaceholder')}
                    disabled={retentionSaving}
                    aria-label={t('versionPanel.retention.inputLabel')}
                    className="themed-input border rounded px-1.5 py-0.5 text-[10px] w-16 outline-none focus:border-blue-500 disabled:opacity-50"
                  />
                  <button
                    onClick={handleSetRetention}
                    disabled={retentionSaving || parsedRetentionInput == null}
                    className="px-2 py-0.5 text-[10px] rounded themed-btn-secondary disabled:opacity-50"
                  >
                    {t('versionPanel.retention.set')}
                  </button>
                  {retention.override != null && (
                    <button
                      onClick={handleClearRetention}
                      disabled={retentionSaving}
                      className="px-2 py-0.5 text-[10px] rounded themed-btn-secondary disabled:opacity-50"
                    >
                      {t('versionPanel.retention.clear')}
                    </button>
                  )}
                  {onManageRetention && (
                    <button
                      onClick={onManageRetention}
                      className="px-2 py-0.5 text-[10px] rounded themed-btn-secondary"
                    >
                      {t('versionPanel.retention.manage')}
                    </button>
                  )}
                </div>
                {retentionInput.trim() !== '' && parsedRetentionInput == null && (
                  <p className="mt-1 text-[10px] text-red-400">{t('versionPanel.retention.invalid')}</p>
                )}
                {purgeConfirm.dialog}
                <div className="mt-1"><RetentionPurgeReceipt outcome={purgeOutcome} testId="file-retention-purged" /></div>
                {retentionActionError && (
                  <p className="mt-1 text-[10px] text-red-400">{retentionActionError}</p>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}
